// src/pages/Pricing.tsx — matches iOS PricingView
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Check, LogOut } from "lucide-react";
import { apiService } from "../services/api";
import PaymentModal, { CheckoutParams } from "../components/PaymentModal";
import { useAuth } from "../context/AuthContext";

type Cycle = "monthly" | "yearly";

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [config, setConfig] = useState<any>(null);
  const [sub, setSub] = useState<any>(null);
  const [cycle, setCycle] = useState<Cycle>("monthly");
  const [checkout, setCheckout] = useState<CheckoutParams | null>(null);
  const [pendingPlan, setPendingPlan] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [submittingPlan, setSubmittingPlan] = useState<string | null>(null);

  // Theme State
  const [isDarkMode, setIsDarkMode] = useState(
    document.documentElement.classList.contains("dark")
  );

  const toggleTheme = () => {
    if (isDarkMode) {
      document.documentElement.classList.remove("dark");
      setIsDarkMode(false);
    } else {
      document.documentElement.classList.add("dark");
      setIsDarkMode(true);
    }
  };

  const handleLogout = async () => {
    try {
      if (typeof apiService?.logout === "function") {
        apiService.logout().catch(() => {});
      }
      localStorage.clear();
      sessionStorage.clear();
      navigate("/login", { replace: true });
    } catch (e) {}
  };

  const load = async () => {
    try {
      const [c, s] = await Promise.all([apiService.getBillingConfig(), apiService.getSubscription()]);
      setConfig(c);
      setSub(s);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const plans: any[] = config?.plans ?? [];
  const currentPlan = (sub?.planName || sub?.plan?.name || "FREE").toUpperCase();
  const cancelAtEnd = sub?.cancelAtPeriodEnd ?? sub?.cancel_at_period_end ?? false;

  const subscribe = async (plan: any) => {
    const planName = (plan.name || plan.planName || "").toUpperCase();
    const planId = plan.id ?? planName;
    
    setPendingPlan(plan.displayName || planName);
    setSubmittingPlan(planId);
    
    try {
      const result = await apiService.createSubscriptionCheckout(planName, cycle);
      setCheckout(result);
    } catch (e: any) {
      alert(e?.response?.data?.error || e?.message || "Could not start checkout");
    } finally {
      setSubmittingPlan(null);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel your subscription at the end of the billing period?")) return;
    try {
      await apiService.cancelSubscription(false);
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.error || "Could not cancel");
    }
  };

  const priceLabel = (p: any) =>
    cycle === "monthly"
      ? p.formattedMonthly ?? `${p.currencySymbol ?? "$"}${p.localMonthlyPrice ?? p.monthlyPrice ?? 0}`
      : p.formattedYearly ?? `${p.currencySymbol ?? "$"}${p.localYearlyPrice ?? p.yearlyPrice ?? 0}`;

  return (
    <div className="min-h-screen bg-[#f8f9fa] dark:bg-[#0a0a0a] text-black dark:text-white font-['Product_Sans'] transition-colors duration-300">
      
      {/* Navbar */}
      <nav className="
        flex items-center justify-between
        h-[72px] px-6 md:px-10
        bg-white dark:bg-black
        border-b border-gray-200 dark:border-[#2C2C2C]
        transition-colors duration-300
        sticky top-0 z-50
      ">
        <div className="flex-1 flex justify-start">
          <span 
            onClick={() => navigate("/dashboard")}
            className="font-['Caviar_Dreams'] text-[24px] md:text-[28px] text-black dark:text-white tracking-widest cursor-pointer"
          >
            VASTAVIK
          </span>
        </div>

        <div className="hidden md:flex flex-1 justify-center items-center gap-8 md:gap-12 h-full">
          {(["dashboard", "library", "upload", "profile"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => navigate(tab === "dashboard" ? "/dashboard" : `/${tab}`)}
              className={`relative h-full flex flex-col items-center justify-center font-['Product_Sans'] text-[15px] transition-colors ${
                tab === "profile" 
                  ? "text-black dark:text-white font-bold"
                  : "text-gray-500 dark:text-[#A0A0A0] hover:text-black dark:hover:text-white font-normal"
              }`}
            >
              <span className="capitalize pb-1">{tab}</span>
              {tab === "profile" && (
                <span className="absolute bottom-0 left-0 w-full h-[2px] bg-black dark:bg-white rounded-t-sm" />
              )}
            </button>
          ))}
        </div>

        <div className="flex-1 flex justify-end items-center gap-4">
          <button
            onClick={toggleTheme}
            title="Toggle Theme"
            className="text-gray-400 hover:text-black dark:hover:text-white transition-colors flex items-center justify-center"
          >
            {isDarkMode ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
            )}
          </button>
          <div className="hidden sm:block text-[13px] text-gray-700 dark:text-[#E0E0E0] font-['Product_Sans'] truncate max-w-[150px] lg:max-w-[200px]">
            {user?.email ?? ""}
          </div>
          <button
            onClick={handleLogout}
            title="Log out"
            className="text-[#E57373] hover:opacity-80 transition-opacity flex items-center justify-center ml-2"
          >
            <LogOut size={20} strokeWidth={2} />
          </button>
        </div>
      </nav>

      <div className="max-w-[1104px] mx-auto p-6 md:p-10 pb-24">
        {/* Header */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-black dark:hover:text-white transition mb-8"
        >
          <ChevronLeft size={16} />
          <span>Back</span>
        </button>

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <h1 className="font-['Caviar_Dreams'] text-[40px] md:text-[44px] leading-tight font-bold text-black dark:text-white mb-3">
              Subscriptions & Plans
            </h1>
            <p className="text-[15px] text-gray-500 dark:text-gray-400 font-light">
              Choose the plan that fits your needs.
            </p>
          </div>

          {/* Cycle toggle */}
          <div className="inline-flex p-1 rounded-full border border-gray-200 dark:border-[#2C2C2C] bg-white dark:bg-[#121212] self-start md:self-auto shadow-sm dark:shadow-none">
            {(["monthly", "yearly"] as Cycle[]).map((c) => (
              <button
                key={c}
                onClick={() => setCycle(c)}
                className={`px-6 py-2 rounded-full text-[13px] font-medium capitalize transition ${
                  cycle === c ? "bg-black text-white dark:bg-white dark:text-black shadow-sm" : "text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-gray-200"
                }`}
              >
                {c}{c === "yearly" ? " - Save" : ""}
              </button>
            ))}
          </div>
        </div>

        {/* Current subscription */}
        {currentPlan !== "FREE" && (
          <div className="p-5 rounded-[12px] bg-white dark:bg-[#121212] border border-gray-200 dark:border-[#2C2C2C] mb-10 flex flex-wrap items-center gap-x-6 gap-y-2 shadow-sm dark:shadow-none">
            <div>
              <div className="text-[11px] text-gray-500 tracking-wider uppercase mb-1">Current plan</div>
              <div className="text-[16px] font-semibold text-black dark:text-white">{currentPlan}</div>
            </div>
            {cancelAtEnd ? (
              <div className="text-[13px] text-yellow-600 dark:text-yellow-500 sm:ml-auto">Cancels at the end of the period</div>
            ) : (
              <button onClick={cancel} className="text-[13px] text-red-600 dark:text-red-500 hover:text-red-500 dark:hover:text-red-400 sm:ml-auto transition">
                Cancel subscription
              </button>
            )}
          </div>
        )}

        {/* Plans Grid */}
        {loading ? (
          <div className="grid gap-[24px] sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-[280px] rounded-[12px] bg-gray-200 dark:bg-[#121212] border border-gray-300 dark:border-[#2C2C2C] animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid gap-[24px] sm:grid-cols-2 lg:grid-cols-3 items-stretch">
            {plans.map((p, i) => {
              const planName = (p.name || p.planName || "").toUpperCase();
              const isCurrent = planName === currentPlan;
              const isSubmitting = submittingPlan === (p.id ?? planName);
              const popular = p.isPopular ?? p.is_popular;

              return (
                <div
                  key={p.id ?? i}
                  className={`relative p-[32px] rounded-[8px] flex flex-col shadow-sm dark:shadow-none ${
                    popular ? "border border-black dark:border-white bg-white dark:bg-[#121212]" : "border border-gray-200 dark:border-[#2C2C2C] bg-white dark:bg-[#121212]"
                  }`}
                >
                  {popular && (
                    <div className="absolute -top-[12px] right-6 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold px-3 py-1.5 rounded-full uppercase tracking-wider">
                      Popular
                    </div>
                  )}

                  <div className="mb-6">
                    <div className="font-['Caviar_Dreams'] text-[18px] font-bold text-black dark:text-white mb-1">
                      {p.displayName ?? planName}
                    </div>
                    <div className="text-[11px] text-gray-500 dark:text-gray-400 tracking-wider uppercase">
                      {p.isUnlimited ?? p.is_unlimited ? "Unlimited data capacity" : `${p.storageGb ?? p.storage_gb ?? 0}GB data capacity`}
                    </div>
                  </div>

                  <div className="mb-8">
                    <span className="text-[36px] font-bold text-black dark:text-white">{priceLabel(p)}</span>
                    <span className="text-[14px] text-gray-500 font-light ml-1">
                      /{cycle === "monthly" ? "mo" : "yr"}
                    </span>
                  </div>

                  {Array.isArray(p.features) && p.features.length > 0 && (
                    <ul className="space-y-2 mb-8 mt-[-16px]">
                      {p.features.slice(0, 5).map((f: string, k: number) => (
                        <li key={k} className="flex items-center gap-3 text-[14px] text-gray-700 dark:text-gray-300 font-light">
                          <Check size={14} className="text-black dark:text-white shrink-0" /> {f}
                        </li>
                      ))}
                    </ul>
                  )}

                  <button
                    disabled={isCurrent || isSubmitting}
                    onClick={() => subscribe(p)}
                    className={`w-full mt-auto h-[38px] px-[16px] rounded-[4px] font-['Product_Sans'] font-medium text-[16px] leading-[12px] tracking-[0.6px] uppercase transition flex items-center justify-center ${
                      isCurrent
                        ? "border border-gray-300 dark:border-[#8E9192] text-gray-400 dark:text-[#8E9192] cursor-default"
                        : isSubmitting
                        ? "bg-gray-100 dark:bg-[#2C2C2C] text-black dark:text-white cursor-not-allowed border border-gray-200 dark:border-[#2C2C2C]"
                        : popular
                        ? "bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200"
                        : "border border-gray-300 dark:border-[#8E9192] text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#1A1A1A]"
                    }`}
                  >
                    {isCurrent ? (
                      "CURRENT PLAN"
                    ) : isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-black dark:text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading...
                      </>
                    ) : (
                      "SUBSCRIBE"
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {checkout && (
        <PaymentModal
          checkout={checkout}
          title={`Subscribe — ${pendingPlan}`}
          subtitle={checkout.formattedPrice ? `${checkout.formattedPrice} / ${cycle === "monthly" ? "month" : "year"}` : undefined}
          payLabel={checkout.formattedPrice ? `Pay ${checkout.formattedPrice}` : "Pay"}
          onClose={() => setCheckout(null)}
          onSuccess={async () => { setCheckout(null); await load(); }}
        />
      )}
    </div>
  );
}